"use client"

import { useCallback, useEffect, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"

type Testimonial = {
  name: string
  role: string
  avatar: string
  content: string
}

const testimonials: Testimonial[] = [
  {
    name: "Salem Wasike",
    role: "UX/UI Designer",
    avatar: "/images/salem-wasike.jpeg",
    content: "It's so intelligent. Actually I'll be using it to polish my communication skills.",
  },
  {
    name: "Andrew Ochieng",
    role: "Software Developer",
    avatar: "/images/andrew-ochieng.png",
    content: "This is super awesome! I like it, got some good insights for a pitch I intend to make. This will go a long way in improving soft skills of many devs & in corporate space generally.",
  },
  {
    name: "Boniface Munga",
    role: "AI Developer",
    avatar: "/images/boniface-munga.jpg",
    content: "It's like having a sparring partner who never gets tired! I've run dozens of mock product pitches, and each one made me 10x more confident.",
  },
  {
    name: "Vicky",
    role: "Software Engineer",
    avatar: "/images/vicky.jpg",
    content: "This AI-powered practice platform is truly impressive. The design is clean and engaging, and the concept of preparing users for high-stakes moments through roleplay scenarios is both innovative and practical. A powerful tool that builds confidence and preparation.",
  },
  {
    name: "Winrose",
    role: "Professional",
    avatar: "/images/winrose.jpg",
    content: "The design is clean and engaging, and the concept of preparing users for high-stakes moments through roleplay scenarios is both innovative and practical. It's clear a lot of thought went into user experience and real-world application.",
  },
]

export function TestimonialCarousel() {
  const [active, setActive] = useState(0)
  const [autoplay] = useState(true)

  const handleNext = useCallback(() => {
    setActive((prev) => (prev + 1) % testimonials.length)
  }, [])

  const handlePrev = () => {
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  const isActive = (index: number) => {
    return index === active
  }

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(handleNext, 5000)
      return () => clearInterval(interval)
    }
  }, [autoplay, handleNext])

  return (
    <section className="relative bg-[#0f0f14] py-24 border-t border-cyan-500/10">
      {/* Animated background elements matching page theme */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative container mx-auto w-full max-w-6xl px-6">
        <div className="mb-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
            {/* Left side - Title and description */}
            <div className="flex flex-col justify-center">
              <div className="inline-block mb-4 px-4 py-2 bg-cyan-500/10 rounded-full border border-cyan-500/20 w-fit">
                <span className="text-cyan-400 text-sm font-semibold">SUCCESS STORIES</span>
              </div>
              <h2 className="text-white mb-4 text-4xl md:text-5xl font-bold">
                Real Results from Real Professionals
              </h2>
              <p className="text-gray-300 text-balance text-lg leading-relaxed">
                Founders, executives, and leaders who practiced their high-stakes moments and won the deals that mattered.
              </p>
            </div>

            {/* Right side - Testimonial card */}
            <div className="relative flex min-h-fit flex-col items-end">
              {/* Navigation Arrows */}
              <div className="mb-4 flex justify-center gap-2">
                <button
                  onClick={handlePrev}
                  className="group/button bg-[#1a1a1f] border-cyan-500/20 hover:border-cyan-400 flex h-10 w-10 items-center justify-center rounded-full border shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-cyan-500/20"
                >
                  <ChevronLeft className="text-cyan-400 h-5 w-5 transition-transform duration-300 group-hover/button:-rotate-12" />
                </button>
                <button
                  onClick={handleNext}
                  className="group/button bg-[#1a1a1f] border-cyan-500/20 hover:border-cyan-400 flex h-10 w-10 items-center justify-center rounded-full border shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-cyan-500/20"
                >
                  <ChevronRight className="text-cyan-400 h-5 w-5 transition-transform duration-300 group-hover/button:rotate-12" />
                </button>
              </div>

              <div className="relative h-[320px] w-full max-w-md">
                {testimonials.map((testimonial, index) => (
                  <div
                    key={testimonial.name}
                    className={`absolute inset-0 transition-all duration-500 ${
                      isActive(index)
                        ? "z-10 opacity-100 translate-y-0"
                        : "z-0 opacity-0 translate-y-8 pointer-events-none"
                    }`}
                  >
                    <div className="bg-[#1a1a1f] rounded-2xl border-2 border-cyan-500/20 hover:border-cyan-400/40 px-6 py-6 shadow-2xl shadow-cyan-500/10 h-full transition-all duration-300">
                      <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                        &quot;{testimonial.content}&quot;
                      </p>

                      <div className="flex items-center gap-3">
                        <Avatar className="size-12 border-2 border-cyan-500/30 shadow-lg">
                          <AvatarImage
                            src={testimonial.avatar}
                            alt={testimonial.name}
                          />
                          <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white font-semibold">
                            {testimonial.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="text-white font-semibold text-lg">
                            {testimonial.name}
                          </div>
                          <span className="text-cyan-400 text-sm font-medium">
                            {testimonial.role}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
